import { useEffect, useState, useCallback } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";

import { WEEKDAY_ORDER } from "../constants";

export const useWeekdayActivityCount = (activeFile = null) => {
  // Use the global API hook for automatic filter integration
  const api = useGlobalAPI(activeFile);
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");
  const [chartData, setChartData] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  const fetchMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use original fast cached endpoints for metadata loading
      const [typesResponse, activitiesResponse] = await Promise.all([
        api.get('/object-types', {}, {}, 'Object Types'),
        api.get('/object-type-activities', {}, {}, 'Object Type Activities'),
      ]);
      
      // Extract data from responses
      const allTypes = typesResponse.objectTypes || [];
      const allActivities = activitiesResponse.objectTypeActivities || {};
      
      // Apply client-side filtering based on current global filters
      const activeFilters = api.currentFilters;
      let filteredTypes = allTypes;
      let filteredActivities = allActivities;
      
      // Apply object type filter if specified
      if (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) {
        filteredTypes = allTypes.filter(type => activeFilters.objectTypes.includes(type));
        
        // Filter activities to only include activities for filtered object types
        filteredActivities = {};
        for (const type of filteredTypes) {
          if (allActivities[type]) {
            filteredActivities[type] = allActivities[type];
          }
        }
      }
      
      console.log("📊 Metadata loaded:", { 
        allTypes: allTypes.length, 
        filteredTypes: filteredTypes.length,
        hasFilters: activeFilters.objectTypes && activeFilters.objectTypes.length > 0
      });
      
      setObjectTypes(filteredTypes);
      setObjectTypeActivities(filteredActivities);
      setIsMetadataLoaded(true);
      setDropdownVisible(true); 
    } catch (err) {
      console.error("Error loading dropdown data:", err);
      setError("Failed to load dropdown data.");
      setIsMetadataLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setIsMetadataLoaded(false);
    setObjectTypes([]);
    setObjectTypeActivities({});
    setSelectedObjectType("");
    setSelectedActivity("");
    setChartData([]);
    setError(null);
    setDropdownVisible(false);
  }, [activeFile]);

  // Auto-reload disabled for now to focus on core functionality

  useEffect(() => {
    if (selectedObjectType && selectedActivity) {
      setLoading(true);
      setError(null);
      
      // Check if filters are meaningful and should use the POST endpoint
      const activeFilters = api.currentFilters;
      const hasFilters = activeFilters && (
        (activeFilters.objectTypes && activeFilters.objectTypes.length > 0) ||
        (activeFilters.dateRange && activeFilters.dateRange.enabled && 
         (activeFilters.dateRange.startDate || activeFilters.dateRange.endDate)) ||
        (activeFilters.eventAttributeFilters && activeFilters.eventAttributeFilters.some(f => f.enabled)) ||
        (activeFilters.activityFilters && activeFilters.activityFilters.enabled && 
         ((activeFilters.activityFilters.included && activeFilters.activityFilters.included.length > 0) ||
          (activeFilters.activityFilters.excluded && activeFilters.activityFilters.excluded.length > 0))) ||
        (activeFilters.leadTimeRange && activeFilters.leadTimeRange.enabled &&
         (activeFilters.leadTimeRange.min !== null || activeFilters.leadTimeRange.max !== null)) ||
        (activeFilters.objectAttributePresenceFilters && activeFilters.objectAttributePresenceFilters.enabled &&
         activeFilters.objectAttributePresenceFilters.attributeNames?.length > 0) ||
        (activeFilters.variantFilter && activeFilters.variantFilter.enabled &&
         activeFilters.variantFilter.variantActivitiesList && activeFilters.variantFilter.variantActivitiesList.length > 0)
      );
      
      // Use appropriate endpoint based on whether filters are applied
      const apiPromise = hasFilters 
        ? api.post('/activity-weekday-stats', {
            activity_name: selectedActivity,
            object_type: selectedObjectType
          }, {}, 'Weekly Activity Stats (Filtered)')
        : api.get('/activity-weekday-stats', {
            activity_name: selectedActivity,
            object_type: selectedObjectType
          }, {}, 'Weekly Activity Stats (Unfiltered)');
      
      apiPromise
        .then((response) => {
          const stats = Array.isArray(response.stats) ? response.stats : [];
          const sortedData = WEEKDAY_ORDER.map((day) => {
            const item = stats.find((entry) => entry.weekday === day);
            return {
              weekday: day,
              median: item?.median || 0,
            };
          });
          console.log("📊 Chart data loaded:", { 
            hasFilters, 
            statsCount: stats.length, 
            chartDataCount: sortedData.length 
          });
          setChartData(sortedData);
        })
        .catch((err) => {
          console.error("Error fetching weekly activity stats:", err);
          setError("Failed to load chart data. Try again.");
          setChartData([]);
        })
        .finally(() => setLoading(false));
    } else {
      setChartData([]);
    }
  }, [selectedObjectType, selectedActivity, api.currentFilters, activeFile]);

  return {
    objectTypes,
    objectTypeActivities,
    selectedObjectType,
    selectedActivity,
    setSelectedObjectType,
    setSelectedActivity,
    dropdownVisible,
    setDropdownVisible,
    chartData,
    loading,
    error,
    isMetadataLoaded,
    fetchMetadata,
  };
};